import { useStore } from 'hooks/useStore';
import { useState } from 'react';

export const useSalesPlan = () => {
  const {
    MSPlanStore: {
      conceptDetails: _conceptDetails,
      isSalesDetailsLoading,
      isSalesPlanDataLoading,
      SalesPlansalesSummaryData: salesSummaryData,
      salesplanViewData,
      salesPlanPeriod,
      salesplanPlanningPeriod,
      isSalesplanPlanningPeriodLoading,
      setSalesplanPlaningPeriod,
    },
  } = useStore();

  const [directSalesPlanCOT, setDirectSalesPlanCOT] = useState<{
    label: string;
    value: string;
  }>({ label: 'CMOP', value: 'cmop' });
  const [indirectSalesPlanCOT, setindirectSalesPlanCOT] = useState<{
    label: string;
    value: string;
  }>({
    label: 'CMOP',
    value: 'cmop',
  });

  // get future 15 months date
  const getFutureMonths = () => {
    const arr: { label: string; dateString: string }[] = [];
    const [year, month, _day] = salesPlanPeriod.code.split('-');
    const startMonth = Number(month);
    const startYear = Number(year);
    const monthShortHand = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    for (let i = 0; i < 15; i++) {
      const month = (startMonth + i) % 12;
      const year = startYear + Math.floor((startMonth + i) / 12);
      arr.push({
        label: `${monthShortHand[month]}-${year}`,
        dateString: `${year}-${month >= 9 ? month + 1 : `0${month + 1}`}-01`,
      });
    }

    return arr;
  };
  const salesPlanningMonth: { label: string; dateString: string }[] = getFutureMonths();

  const handleOptionChange = (option: { label: string; code: string }) => {
    setSalesplanPlaningPeriod(option);
  };

  const getSalesSummaryVal = (cot: string, type: string, cat: string) => {
    if (isSalesDetailsLoading) return undefined;

    // Handle cases where data might be undefined or empty
    if (!salesSummaryData || salesSummaryData.length === 0) {
      return 'NA';
    }

    const val = salesSummaryData.find(item => item.COT.toLowerCase() === cot.toLowerCase());
    if (!val) return 'NA';

    switch (type) {
      case 'YTD':
        if (cat === 'direct') {
          if (val.TotalAnnualSalesVolumeDirect !== null) {
            if (val.TotalAnnualSalesVolumeDirect === 0) return val.TotalAnnualSalesVolumeDirect;
            return val.TotalAnnualSalesVolumeDirect;
          } else {
            return 'NA';
          }
        } else if (cat === 'indirect') {
          if (val.TotalAnnualSalesVolumeIndirect !== null) {
            if (val.TotalAnnualSalesVolumeIndirect === 0) return val.TotalAnnualSalesVolumeIndirect;
            return val.TotalAnnualSalesVolumeIndirect;
          } else {
            return 'NA';
          }
        }
        break;
      case '3MA':
        if (cat === 'direct') {
          if (val.LastThreeMonthAvgDirect !== null) {
            if (val.LastThreeMonthAvgDirect === 0) return val.LastThreeMonthAvgDirect;
            return val.LastThreeMonthAvgDirect;
          } else {
            return 'NA';
          }
        } else if (cat === 'indirect') {
          if (val.LastThreeMonthAvgIndirect !== null) {
            if (val.LastThreeMonthAvgIndirect === 0) return val.LastThreeMonthAvgIndirect;
            return val.LastThreeMonthAvgIndirect;
          } else {
            return 'NA';
          }
        }
        break;
      case 'PM':
        if (cat === 'direct') {
          if (val.LastMonthSalesVolumeDirect !== null) {
            if (val.LastMonthSalesVolumeDirect === 0) return val.LastMonthSalesVolumeDirect;
            return val.LastMonthSalesVolumeDirect;
          } else {
            return 'NA';
          }
        } else if (cat === 'indirect') {
          if (val.LastMonthSalesVolumeIndirect !== null) {
            if (val.LastMonthSalesVolumeIndirect === 0) return val.LastMonthSalesVolumeIndirect;
            return val.LastMonthSalesVolumeIndirect;
          } else {
            return 'NA';
          }
        }
        break;
      default:
        return 'NA';
    }
  };

  const getValues = (cot: string, planningMonth: string, cat: string, need: string) => {
    if (salesplanViewData.GFC === 0) return '-';
    const cotSalesPlan = salesplanViewData.sales_plan_data.filter(
      item => item.COT.toLowerCase() === cot.toLowerCase()
    )[0];
    const salesplancat = cotSalesPlan?.SellingCategories.filter(
      item => item.SellingCategory.toLowerCase() === cat.toLowerCase()
    )[0];
    const salesplanDataCurrent = salesplancat?.sales_plan_details.filter(
      item => item.Month === planningMonth
    );
    let salesPlanPrevious: {
      Adjustment: number;
      SalesPlan: number;
      Month: string;
      Note: string | null;
      MarketSalesForecast: number;
      TargetMarketShare: number;
      MarketSharePlan: number;
    }[] = [];

    if (salesplanDataCurrent && salesplanDataCurrent.length === 0) {
      const previousMonth = new Date(planningMonth);
      let isPrevFound = false;

      // Set a limit for the number of iterations to prevent infinite loop
      const maxIterations = 12; // Limit to 12 months (1 year)
      let iterations = 0;
      while (!isPrevFound && iterations < maxIterations) {
        previousMonth.setMonth(previousMonth.getMonth() - 1);
        const prevPlanningDate = previousMonth.toISOString().split('T')[0];
        const [year, month, _day] = prevPlanningDate.split('-');

        salesPlanPrevious = salesplancat?.sales_plan_details.filter(
          item => item.Month === `${year}-${month}-01`
        );

        if (salesPlanPrevious && salesPlanPrevious.length !== 0) {
          isPrevFound = true;
        }
        iterations++; // Increment the iteration count
      }
    }

    const salesplanData =
      salesplanDataCurrent && salesplanDataCurrent.length > 0
        ? salesplanDataCurrent
        : salesPlanPrevious;

    if ((salesplanData && salesplanData.length === 0) || salesplanData === undefined) {
      return '-';
    } else {
      if (need === 'marketForecast' && salesplanData) {
        const val = salesplanData[0]?.MarketSalesForecast;
        return typeof val === 'number' ? val.toLocaleString('en-US') : val;
      } else if (need === 'adjforecast' && salesplanData) {
        return Math.ceil(salesplanData[0]?.MarketSalesForecast + salesplanData[0].Adjustment).toLocaleString('en-US');
      } else if (need === 'tmsvalue' && salesplanData) {
        return String(Math.ceil(salesplanData[0]?.TargetMarketShare * 100)) + '%';
      } else if (need === 'msplan' && salesplanData) {
        return String(Math.round(salesplanData[0]?.MarketSharePlan * 100)) + '%';
      } else if (need === 'euforecast' && salesplanData) {
        return Math.ceil(salesplanData[0]?.SalesPlan).toLocaleString('en-US');
      }
    }
  };

  return {
    directSalesPlanCOT,
    indirectSalesPlanCOT,
    isSalesDetailsLoading,
    salesPlanningMonth,
    salesSummaryData,
    isSalesPlanDataLoading,
    salesPlanPeriod,
    salesplanPlanningPeriod,
    isSalesplanPlanningPeriodLoading,
    getValues,
    getSalesSummaryVal,
    setindirectSalesPlanCOT,
    setDirectSalesPlanCOT,
    handleOptionChange,
  };
};
